'use client';

import React, { useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ServiceTier {
  id: string;
  sectionLabel: string;
  tier: string;
  name: string;
  price: string;
  tagline: string;
  bestFor: string;
  features: string[];
  bonuses?: string[];
  revisions: string;
  recommended?: boolean;
  icon: string;
}

const careerDocTiers: ServiceTier[] = [
  {
    id: 'cd1',
    sectionLabel: 'Executive & Board-Level Career Branding Packages',
    tier: 'Tier 1',
    name: 'Enterprise Leadership Suite',
    price: '$700 – $900',
    tagline: 'Eliminates "Operational Traps" on your resume — where past achievements read like senior tactical execution rather than high-stakes enterprise governance.',
    bestFor: 'Senior Directors, Vice Presidents, and Enterprise Functional Heads transitioning to expanded organizational oversight.',
    features: [
      'Complete Structural Redesign: Modern visual hierarchy optimized for linear, rapid 6-second C-suite evaluations and deterministic ATS parsing.',
      'Scope & Mandate Framing: Articulation of P&L scale, total enterprise budget oversight, headcount, and multi-facility operational authority.',
      'C-A-R Transformation: Refactoring responsibilities into quantified revenue, cost reduction, EBITDA preservation, and organizational risk-mitigation metrics.',
      'Strategic Positioning Summary: A high-conviction executive summary framing your unique leadership value proposition and market relevance.',
      'Keyword Indexing: Embedding high-level search terms aligned with enterprise leadership, governance, and business-unit strategy.',
    ],
    revisions: '2 structured revision rounds',
    icon: 'DocumentTextIcon',
  },
  {
    id: 'cd2',
    sectionLabel: 'Executive & Board-Level Career Branding Packages',
    tier: 'Tier 2',
    name: 'C-Suite & Strategic Executive Suite',
    price: '$1,000 – $1,300',
    tagline: 'Solves the critical credibility and messaging gap when search firms and executive nomination committees evaluate your multi-decade track record.',
    bestFor: 'Managing Directors, Executive Vice Presidents, C-Suite Officers (COO, CFO, CIO, CTO, CMO), and Corporate Advisors.',
    features: [
      'Enterprise C-Suite Resume Rebuild: Comprehensive narrative repositioning focused on institutional turnaround, market expansion, capital planning, and corporate vision.',
      'Bespoke Strategic Cover Letter: A non-templated, pain-point-driven business brief structured to speak directly to board priorities, PE sponsors, or CEO mandates.',
      'Multi-Stakeholder Authority Alignment: Reframing achievements to spotlight executive consensus-building, board interaction, regulatory oversight, and investor relations.',
      'Cross-Functional & Culture Integration: Evidence of building resilient leadership benches, driving digital/operational transformation, and leading M&A or structural changes.',
      'Executive Value Proposition: Crisp definition of your business thesis — what operational friction you permanently eliminate for an enterprise.',
    ],
    revisions: '2 collaborative revision rounds with consultative feedback',
    recommended: true,
    icon: 'BriefcaseIcon',
  },
  {
    id: 'cd3',
    sectionLabel: 'Executive & Board-Level Career Branding Packages',
    tier: 'Tier 3',
    name: 'Board of Directors & Governance Suite',
    price: '$1,400 – $2,000',
    tagline: 'Eliminates the risk of presenting an operational resume for a fiduciary seat by creating a dedicated Board Bio, Board CV, and outreach strategy.',
    bestFor: 'Independent Directors, Board Trustees, Advisory Board Candidates, and C-Suite Executives seeking external board appointments.',
    features: [
      '1-Page Executive / Board Biography: Narrative, publication-ready asset detailing your fiduciary strengths, strategic career case studies, and board value proposition.',
      'Board-Standard Master CV / Resume: Tailored structure accentuating corporate governance, audit/compensation committee qualifications, shareholder value creation, and ESG/cyber risk oversight.',
      'Fiduciary & Capital Allocation Focus: Explicit framing around capital allocation discipline, compliance accountability, enterprise risk stewardship, and macroeconomic foresight.',
      'Nomination Committee & Search Firm Alignment: Language and format mapped to NACD benchmarks and global search firm standards (Spencer Stuart, Heidrick & Struggles, Egon Zehnder, Korn Ferry).',
      'Executive Networking & Board Outreach Scripts: High-converting outreach scripts designed to initiate discreet advisory dialogues with chairs, PE talent partners, and search executives.',
    ],
    revisions: '3 comprehensive revision rounds with direct consultative alignment',
    icon: 'ClipboardDocumentListIcon',
  },
];

const applicationDocTiers: ServiceTier[] = [
  {
    id: 'ad1',
    sectionLabel: 'Tailored Application Document Packages',
    tier: 'Tier 1',
    name: 'Complete Targeted Application Suite',
    price: '$450–$650',
    tagline: 'Creates a unified, targeted package built around specific opportunity requirements.',
    bestFor: 'Candidates needing multiple application documents prepared for a specific opportunity.',
    features: [
      'Complete ATS-Optimized Target Resume/CV',
      'Tailored Strategic Cover Letter',
      'All Specifically Requested Custom Documents (Executive Bio, Statement of Intent, Leadership Philosophy, Value Brief, or Career Narrative)',
      'Job-description & employer requirement alignment',
    ],
    revisions: 'Collaborative revision rounds included',
    recommended: true,
    icon: 'DocumentDuplicateIcon',
  },
  {
    id: 'ad2',
    sectionLabel: 'Tailored Application Document Packages',
    tier: 'Tier 2',
    name: 'Application Suite + LinkedIn Alignment',
    price: '$800–$1,500',
    tagline: 'Eliminates inconsistencies between submitted documents and LinkedIn presence across all channels.',
    bestFor: 'Candidates wanting application materials and online presence to tell one consistent career story.',
    features: [
      'Everything in Tier 1 (Target Resume/CV, Cover Letter & All Custom Documents)',
      'Full LinkedIn Profile Overhaul: SEO-driven headline, strategic 1st-person About, repositioned Experience & skills optimization',
      'Professional delivery in editable .docx and PDF',
      '2 collaborative revision rounds across package',
      'Strategic Recruiter & Decision-Maker outreach scripts',
      'Priority drafting turnaround',
      'Unlimited collaborative revisions within 10-day window',
    ],
    bonuses: [
      'Job Search Master Prompt Suite',
      'ATS Resume Master Template (Lifetime free updates)',
      'Power Achievement Bullet Bank (60+ Frameworks)',
      'Professional Bio Template (Networking/Speaking)',
      'Behavioral Interview (STAR) Simulator Guide',
      'Inbound LinkedIn Playbook',
      'Lifetime Access to Tier 1 Toolkits & Prompts',
    ],
    revisions: 'Unlimited collaborative revisions within 10-day window',
    recommended: true,
    icon: 'GlobeAltIcon',
  },
];

interface BooksShelfProps {
  onReadChapter?: () => void;
}

const BooksShelf: React.FC<BooksShelfProps> = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.08 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const renderTierCard = (tier: ServiceTier, idx: number) => (
    <div
      key={tier.id}
      className={`reveal-scale relative rounded-2xl p-8 border transition-all duration-500 group flex flex-col ${
        tier.recommended
          ? 'border-gold/40 bg-obsidian-card shadow-gold-lg'
          : 'border-parchment/8 bg-obsidian-card hover:border-gold/20'
      }`}
      style={{ transitionDelay: `${idx * 120}ms` }}
    >
      {/* Recommended badge */}
      {tier.recommended && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gold text-obsidian text-xs font-body font-bold tracking-widest uppercase px-4 py-1 rounded-full whitespace-nowrap">
          ⭐ Most Recommended
        </div>
      )}

      {/* Icon + Tier label */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tier.recommended ? 'bg-gold/20' : 'bg-gold/10'}`}>
          <Icon name={tier.icon as any} size={20} className="text-gold" />
        </div>
        <span className="text-xs font-body font-semibold text-gold/70 tracking-widest uppercase">{tier.tier}</span>
      </div>

      {/* Name & Price */}
      <h3 className="font-display font-light text-parchment text-2xl leading-snug mb-2">{tier.name}</h3>
      <p className="font-display italic text-gold text-3xl font-light mb-3">{tier.price}</p>
      <p className="font-body text-parchment/50 text-sm leading-relaxed mb-2 pb-4 border-b border-parchment/8">{tier.tagline}</p>

      {/* Best For */}
      <div className="flex items-start gap-2 mb-6 mt-2">
        <Icon name="UserIcon" size={13} className="text-gold/60 shrink-0 mt-0.5" />
        <p className="text-parchment/45 text-xs font-body leading-relaxed italic">{tier.bestFor}</p>
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-6 flex-1">
        {tier.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3">
            <Icon name="CheckIcon" size={16} className="text-gold shrink-0 mt-0.5" />
            <span className="text-parchment/65 text-sm font-body leading-relaxed">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Free Bonuses */}
      {tier.bonuses && tier.bonuses.length > 0 && (
        <div className="mb-6 bg-gold/5 rounded-xl p-4 border border-gold/10">
          <p className="text-gold text-xs font-body font-bold tracking-widest uppercase mb-3">Free Self-Update Bonuses</p>
          <ul className="space-y-2">
            {tier.bonuses.map((bonus, i) => (
              <li key={i} className="flex items-start gap-2">
                <Icon name="GiftIcon" size={12} className="text-gold/70 shrink-0 mt-0.5" />
                <span className="text-parchment/55 text-xs font-body leading-relaxed">{bonus}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Revisions */}
      <div className="flex items-start gap-3 mb-8 bg-gold/5 rounded-xl p-4 border border-gold/10">
        <Icon name="ArrowPathIcon" size={15} className="text-gold shrink-0 mt-0.5" />
        <span className="text-parchment/60 text-xs font-body leading-relaxed">{tier.revisions}</span>
      </div>

      {/* CTA */}
      <a
        href="mailto:jc.careerwriter@gmail.com"
        className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-body font-semibold transition-all duration-300 ${
          tier.recommended
            ? 'bg-gold text-obsidian hover:bg-gold-light hover:shadow-gold'
            : 'border border-gold/30 text-gold hover:bg-gold/10'
        }`}
      >
        Get Started
        <Icon name="ArrowRightIcon" size={14} />
      </a>
    </div>
  );

  return (
    <section ref={sectionRef} id="services" className="py-28 px-6 bg-obsidian-mid relative overflow-hidden">
      <div className="ambient-glow w-[600px] h-[600px] bg-gold/5 top-0 left-1/2 -translate-x-1/2" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20 reveal">
          <span className="text-xs font-body font-semibold text-gold tracking-[0.3em] uppercase mb-4 block">Investment in Your Career</span>
          <h2 className="font-display font-light text-parchment leading-tight mb-5" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>
            Executive & Board-Level<br />
            <span className="italic text-parchment/50">Career Branding Packages.</span>
          </h2>
          <p className="font-body text-parchment/50 text-lg max-w-xl mx-auto leading-relaxed">
            Three precision-engineered tiers built for senior leaders, C-suite officers, and board candidates. Each package is designed to establish institutional authority and position you for the highest-stakes opportunities.
          </p>
        </div>

        {/* Section 1: Executive & Board-Level Career Branding Packages */}
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-10 reveal">
            <div className="h-px flex-1 bg-parchment/10" />
            <span className="text-xs font-body font-semibold text-gold/70 tracking-[0.25em] uppercase px-4">Executive & Board-Level Career Branding Packages</span>
            <div className="h-px flex-1 bg-parchment/10" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {careerDocTiers.map((tier, idx) => renderTierCard(tier, idx))}
          </div>
        </div>

        {/* Section 2: Tailored Application Document Packages */}
        <div className="mb-14">
          <div className="flex items-center gap-4 mb-10 reveal">
            <div className="h-px flex-1 bg-parchment/10" />
            <span className="text-xs font-body font-semibold text-gold/70 tracking-[0.25em] uppercase px-4">Tailored Application Document Packages</span>
            <div className="h-px flex-1 bg-parchment/10" />
          </div>
          <p className="text-center font-body text-parchment/40 text-sm mb-10 max-w-2xl mx-auto reveal">
            Designed for candidates needing custom, multi-asset application packets for specific high-stakes opportunities.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {applicationDocTiers.map((tier, idx) => renderTierCard(tier, idx))}
          </div>
        </div>

        {/* Zero Recurring Fees callout */}
        <div className="reveal rounded-2xl border border-gold/20 bg-gold/5 p-8 text-center mb-10">
          <div className="w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-4">
            <Icon name="CurrencyDollarIcon" size={22} className="text-gold" />
          </div>
          <h3 className="font-display font-light text-parchment text-xl mb-3">Zero Recurring Update Fees</h3>
          <p className="font-body text-parchment/55 text-sm leading-relaxed max-w-2xl mx-auto">
            Your career shouldn't require you to keep paying every time an opportunity changes.{' '}
            <strong className="font-semibold text-gold">Each package gives you reusable templates, bullet banks, and prompt suites that put you back in control.</strong>{' '}
            Reduce the effort, cost, and frustration of constantly rebuilding your application materials.
          </p>
        </div>

        {/* Bottom note */}
        <div className="mt-6 text-center reveal">
          <p className="text-parchment/35 text-sm font-body">
            Not sure which tier is right for you?{' '}
            <a href="mailto:jc.careerwriter@gmail.com" className="text-gold hover:text-gold-light transition-colors underline underline-offset-4">
              Request a complimentary document review
            </a>{' '}
            and Judith will recommend the best fit.
          </p>
        </div>
      </div>
    </section>
  );
};

export default BooksShelf;